import { OrganizationWithHierarchyType } from '../contexts/types';

// ========== ツリーレイアウト定数 ==========
export const NODE_WIDTH = 280;
export const NODE_BASE_HEIGHT = 56;
export const H_GAP = 300;
export const V_GAP = 36;
export const PAD = 40;

/** 組織名の省略文字数 */
export const ORG_NAME_ABBREVIATE_LENGTH = 30;

export interface PositionedNode {
  org: OrganizationWithHierarchyType;
  x: number;
  y: number;
  width: number;
  height: number;
  subtreeMinY: number;
  subtreeMaxY: number;
  children: PositionedNode[];
}

export type TreeBounds = { minX: number; minY: number; width: number; height: number };

/** ツリー全体の描画範囲を算出 */
export function getBounds(trees: PositionedNode[]): TreeBounds {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  const walk = (n: PositionedNode) => {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
    n.children.forEach(walk);
  };
  trees.forEach(walk);
  if (!isFinite(minX)) return { minX: 0, minY: 0, width: 0, height: 0 };
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

/** 親IDをキーにした組織マップを構築 */
export function groupByParent(organizations: OrganizationWithHierarchyType[]): Map<string, OrganizationWithHierarchyType[]> {
  const m = new Map<string, OrganizationWithHierarchyType[]>();
  organizations.forEach((o) => {
    const key = o.parentOrganizationId || '';
    const arr = m.get(key) || [];
    arr.push(o);
    m.set(key, arr);
  });
  return m;
}

/** 子をソートする共通順序 */
function sortOrgs(orgs: OrganizationWithHierarchyType[]): OrganizationWithHierarchyType[] {
  return [...orgs].sort(
    (a, b) => a.organizationName.localeCompare(b.organizationName, 'ja')
  );
}

/**
 * 組織データから描画用ノードを再帰的に構築
 * @param org 組織データ
 * @param orgsByParent 親IDでグルーピングされた組織マップ
 * @param calcHeight ノードの高さを算出する関数（省略時は NODE_BASE_HEIGHT）
 */
export function buildNode(
  org: OrganizationWithHierarchyType,
  orgsByParent: Map<string, OrganizationWithHierarchyType[]>,
  calcHeight?: (org: OrganizationWithHierarchyType) => number
): PositionedNode {
  const childrenOrgs = sortOrgs(orgsByParent.get(org.organizationId) || []);
  const children = childrenOrgs.map((c) => buildNode(c, orgsByParent, calcHeight));
  const height = calcHeight ? calcHeight(org) : NODE_BASE_HEIGHT;
  return { org, x: 0, y: 0, width: NODE_WIDTH, height, subtreeMinY: 0, subtreeMaxY: 0, children };
}

/** サブツリー全体を垂直方向にシフト */
export function shiftSubtree(n: PositionedNode, dy: number): void {
  n.y += dy;
  n.subtreeMinY += dy;
  n.subtreeMaxY += dy;
  n.children.forEach((c) => shiftSubtree(c, dy));
}

/**
 * ツリーレイアウトアルゴリズム
 * 親ノードを子サブツリーの中央に配置し、子ノードを垂直方向に等間隔で配置
 */
export function layoutTree(node: PositionedNode, startX: number, startY: number): PositionedNode {
  node.x = startX;

  if (node.children.length === 0) {
    node.y = startY;
    node.subtreeMinY = startY;
    node.subtreeMaxY = startY + node.height;
    return node;
  }

  const children: PositionedNode[] = [];
  let childY = startY;
  for (const c of node.children) {
    const pos = layoutTree(c, startX + H_GAP, childY);
    children.push(pos);
    childY = pos.subtreeMaxY + V_GAP;
  }
  node.children = children;

  const heights = node.children.map((c) => c.subtreeMaxY - c.subtreeMinY);
  const totalChildrenHeight = heights.reduce((s, h) => s + h, 0) + V_GAP * (node.children.length - 1);

  const firstTop = node.children[0].subtreeMinY;
  const parentCenterY = firstTop + totalChildrenHeight / 2;
  node.y = parentCenterY - node.height / 2;

  let cursorTop = parentCenterY - totalChildrenHeight / 2;
  for (let i = 0; i < node.children.length; i++) {
    const ch = node.children[i];
    const chTop = ch.subtreeMinY;
    const dy = cursorTop - chTop;
    if (dy !== 0) shiftSubtree(ch, dy);
    cursorTop += heights[i] + V_GAP;
  }

  const minY = Math.min(node.y, node.children[0].subtreeMinY);
  const maxY = Math.max(node.y + node.height, node.children[node.children.length - 1].subtreeMaxY);
  node.subtreeMinY = minY;
  node.subtreeMaxY = maxY;

  return node;
}

/**
 * 対象ID群から表示すべきノードIDの集合を算出する
 * （対象ノード自身、祖先チェーン、対象ノードと同じ親を持つ兄弟ノードのみを含む）
 * @param orgList 組織データ一覧
 * @param targetIds 表示範囲の基準となる対象組織ID群
 */
export function getScopedVisibleOrgIds(
  orgList: OrganizationWithHierarchyType[],
  targetIds: string[]
): Set<string> {
  const orgById = new Map(orgList.map((o) => [o.organizationId, o]));
  const visibleIds = new Set<string>();

  targetIds.forEach((targetId) => {
    const target = orgById.get(targetId);
    if (!target) return;

    visibleIds.add(targetId);

    // 兄弟ノード（対象ノード自身の階層のみ）。親が空文字列/NULL、または親ノードが存在しない場合は兄弟展開しない
    if (target.parentOrganizationId && orgById.has(target.parentOrganizationId)) {
      orgList
        .filter((o) => o.parentOrganizationId === target.parentOrganizationId)
        .forEach((o) => visibleIds.add(o.organizationId));
    }

    // 祖先チェーン（親→祖父母→…→ルートまでの経路）
    let currentId = target.parentOrganizationId;
    while (currentId) {
      const ancestor = orgById.get(currentId);
      if (!ancestor) break;
      visibleIds.add(ancestor.organizationId);
      currentId = ancestor.parentOrganizationId;
    }
  });

  return visibleIds;
}

/**
 * 複数のルートツリーを構築しレイアウトする
 * @param rootOrgs ルート組織の配列
 * @param orgsByParent 親IDでグルーピングされた組織マップ
 * @param calcHeight ノード高さ算出関数（省略時は NODE_BASE_HEIGHT）
 */
export function buildAndLayoutTrees(
  rootOrgs: OrganizationWithHierarchyType[],
  orgsByParent: Map<string, OrganizationWithHierarchyType[]>,
  calcHeight?: (org: OrganizationWithHierarchyType) => number
): PositionedNode[] {
  const sorted = sortOrgs(rootOrgs);
  const built = sorted.map((r) => buildNode(r, orgsByParent, calcHeight));
  const laid = built.map((b) => layoutTree(b, 0, 0));
  let shiftY = 0;
  laid.forEach((treeNode) => {
    const dy = shiftY - treeNode.subtreeMinY;
    shiftSubtree(treeNode, dy);
    shiftY = treeNode.subtreeMaxY + V_GAP;
  });
  return laid;
}
